import { createFileRoute } from '@tanstack/react-router';
import { queryOptions, useSuspenseQuery } from '@tanstack/react-query';
import { Suspense } from 'react';

import { PageContainer } from '../components/layout/page-container';
import { latestByTypeQuery, useLatestByType } from '../features/grants/use-latest';
import { bannersQuery, useBanners } from '../features/banners/banners';
import { CommunityPanel, LatestPanels } from '../features/landing/latest-panels';
import {
  latestPostsQuery,
  popularPostsQuery,
  useLatestPosts,
  usePopularPosts,
} from '../features/community/use-latest';
import { api } from '../lib/api';
import { pageHead } from '../lib/seo';
import {
  canonical,
  SITE_NAME,
  HERO_ALT,
  HERO_IMAGE,
  HERO_IMAGE_HEIGHT,
  HERO_IMAGE_SIZES,
  HERO_IMAGE_SRCSET,
  HERO_IMAGE_WIDTH,
  SITE_DESCRIPTION,
  SITE_TITLE,
} from '../lib/site';
import { NumberTicker } from '../components/ui/number-ticker';
import { useHydrated } from '../hooks/use-hydrated';
import { TextAnimate } from '../components/magicui/text-animate';

/**
 * 랜딩.
 *
 * 목록을 그대로 첫 화면에 두는 건 정부 사이트가 이미 하는 일이고, 우리가
 * 다른 점을 아무것도 말하지 않는다. 여기서는 **무엇을 하는 곳인지**를 먼저
 * 말하고 목록으로 보낸다.
 *
 * 숫자는 실측이다 (`/grants/stats`). 랜딩의 숫자를 지어내면 그게 이 제품이
 * 파는 것 — 정확한 금액 — 과 정면으로 모순된다.
 */
interface Stats {
  open: number;
  withAmount: number;
  closingSoon: number;
}

const grantStatsQuery = queryOptions({
  queryKey: ['grant-stats'],
  queryFn: () => api.get<Stats>('/grants/stats'),
});

export const Route = createFileRoute('/')({
  loader: async ({ context: { queryClient } }) => {
    // 네 칸은 실패해도 화면을 안 막는다 — 히어로가 본체고 그건 덤이다.
    const [banners] = await Promise.allSettled([
      queryClient.ensureQueryData(bannersQuery),
      queryClient.ensureQueryData(grantStatsQuery),
      queryClient.ensureQueryData(latestByTypeQuery),
      queryClient.ensureQueryData(latestPostsQuery),
      queryClient.ensureQueryData(popularPostsQuery),
    ]);
    return { hero: banners.status === 'fulfilled' ? banners.value.hero : null };
  },
  head: ({ loaderData }) => {
    const { meta, links } = pageHead({
      title: SITE_TITLE,
      description: SITE_DESCRIPTION,
      url: canonical('/'),
      type: 'website',
      jsonLd: [
        {
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: SITE_NAME,
          url: canonical('/'),
          description: SITE_DESCRIPTION,
        },
      ],
    });
    // 히어로(LCP)를 head 에서 미리 받는다 — HTML 파서가 본문의 img 에 닿기 전에 요청이 나간다.
    // 콘솔이 올린 히어로면 그 파일 하나(srcset 없음), 아니면 기본 두 벌.
    const hero = loaderData?.hero;
    const preload = hero
      ? { rel: 'preload', as: 'image', href: hero.url, fetchPriority: 'high' as const }
      : {
          rel: 'preload',
          as: 'image',
          href: HERO_IMAGE,
          imageSrcSet: HERO_IMAGE_SRCSET,
          imageSizes: HERO_IMAGE_SIZES,
          fetchPriority: 'high' as const,
        };
    return { meta, links: [...links, preload] };
  },
  component: LandingPage,
});

function useStats() {
  return useSuspenseQuery(grantStatsQuery);
}

function Numbers() {
  const { data } = useStats();
  const cells = [
    { n: data.open, label: '열려 있는 공고' },
    { n: data.withAmount, label: '금액 확인 공고' },
    { n: data.closingSoon, label: '이번 주 마감' },
  ];
  return (
    <dl className="grid grid-cols-3 gap-px overflow-hidden rounded-xl bg-gray-200">
      {cells.map((c, i) => (
        <div key={c.label} className="bg-white px-4 py-5 text-center">
          <dt className="sr-only">{c.label}</dt>
          <dd>
            {/* 0 에서 올라간다. SSR 은 완성된 숫자를 내고, 여기서 한 번 세어 올린다. */}
            <NumberTicker
              value={c.n}
              delay={i * 0.1}
              className="block text-2xl font-bold tabular-nums tracking-tight text-gray-900 sm:text-3xl"
            />
            <span className="mt-1 block text-xs text-gray-500">{c.label}</span>
          </dd>
        </div>
      ))}
    </dl>
  );
}

function NumbersFallback() {
  return (
    <div className="grid grid-cols-3 gap-px overflow-hidden rounded-xl bg-gray-200">
      {[0, 1, 2].map((i) => (
        <div key={i} className="bg-white px-4 py-5">
          <div className="mx-auto h-8 w-16 animate-pulse rounded bg-gray-100" />
          <div className="mx-auto mt-2 h-3 w-20 animate-pulse rounded bg-gray-100" />
        </div>
      ))}
    </div>
  );
}

const INTRO = '좋소 과수원은 관심 있는 지역의 꽁돈을 언제 얼마나 따먹을 수 있는지 선별된 싱싱한 정보로 가공해 드립니다.';

function LandingPage() {
  const { data: latest } = useLatestByType();
  // 콘솔이 올린 히어로가 있으면 그것(단일 파일, srcset 없음). head() 의 preload 와 같은 파일.
  const hero = useBanners().hero;
  const { data: latestPosts } = useLatestPosts();
  const { data: popularPosts } = usePopularPosts();
  // 소개 한 줄의 blurIn 은 hydration 뒤에 켠다. 서버 HTML 에 framer 의 initial(투명)을 내면
  // 크롤러와 첫 페인트가 빈 줄을 본다 (docs/SSR.md hydration 규칙 7).
  const hydrated = useHydrated();

  return (
    <PageContainer>
      {/*
        히어로 배너. 문구가 이미지에 구워져 있어 h1 은 sr-only 로 따로 둔다 —
        검색엔진과 화면 낭독기는 그림 속 글자를 못 읽는다. alt 가 같은 말을
        하므로 숨김 텍스트가 아니라 같은 내용의 다른 표현이다.

        폭은 컨테이너를 넘겨 좌우 여백까지 채운다. 2:1 배너가 안쪽에만 있으면
        띠처럼 보이고, 이 그림은 가장자리까지 그려져 있다.
      */}
      {/*
        작은 화면에서는 제목을 실제로 보여준다. 배너 문구가 그림에 구워져
        있어 390px 폭이면 부제가 9px 로 줄어 읽히지 않는다 — 그러면 모바일
        독자는 읽을 수 있는 제목을 하나도 못 본다. 큰 화면에서는 배너가
        같은 말을 하므로 낭독기에만 남긴다.
      */}
      <h1 className="sr-only">
        {SITE_TITLE}
      </h1>
      <img
        src={hero?.url ?? HERO_IMAGE}
        // head() 의 preload 와 같은 srcset — 다르면 브라우저가 다른 파일을 또 받고 LCP 가 그리로 밀린다.
        srcSet={hero ? undefined : HERO_IMAGE_SRCSET}
        sizes={HERO_IMAGE_SIZES}
        width={hero?.width ?? HERO_IMAGE_WIDTH}
        height={hero?.height ?? HERO_IMAGE_HEIGHT}
        alt={HERO_ALT}
        // LCP 요소다. 지연 로딩을 걸면 첫 화면이 늦어진다.
        fetchPriority="high"
        decoding="async"
        className="-mx-4 -mt-4 block w-[calc(100%+2rem)] max-w-none sm:mx-0 sm:mt-0 sm:w-full sm:rounded-xl"
      />

      <section className="py-4 sm:py-5">
        {/* 한 줄. 좁은 화면에서만 줄바꿈을 허용한다 — 390px 에는 안 들어간다. */}
        {/* 검정. 글자 하나씩 흐림에서 또렷해진다(Magic UI blurIn) — 서버 HTML 은 최종 상태를 낸다. */}
        <p className="text-sm leading-relaxed text-black sm:whitespace-nowrap">
          {hydrated ? (
            <TextAnimate as="span" by="character" animation="blurIn" duration={5}>
              {INTRO}
            </TextAnimate>
          ) : (
            <span>{INTRO}</span>
          )}
        </p>
      </section>

      <Suspense fallback={<NumbersFallback />}>
        <Numbers />
      </Suspense>

      {/*
        **히어로 → 소개 한 줄 → 숫자 세 칸 → 유형별 네 칸.**
        각 칸이 자기 주소(`/grants` 등)로 데려가므로 별도의 「공고 보러 가기」 버튼은 두지 않는다.
      */}
      <div className="pt-6">
        <LatestPanels latest={latest} />
      </div>
      {/* 커뮤니티 두 칸(최신 · 인기글). */}
      <div className="grid grid-cols-1 gap-3 pt-3 sm:grid-cols-2">
        <CommunityPanel label="최신" posts={latestPosts} />
        <CommunityPanel label="인기글" posts={popularPosts} />
      </div>
    </PageContainer>
  );
}
